import React, { useEffect, useMemo, useState } from 'react';
import { useAuthContext } from '../hooks/useAuthContext';
import { useArtworksContext } from '../hooks/useArtworksContext';
import ArtworkDetails from './ArtworkDetails';
import ArtworkForm from './ArtworkForm';
import ArtworkSkeleton from './ArtworkSkeleton';
import VirtualizedGallery from './VirtualizedGallery';
import useContainerWidth from '../hooks/useContainerWidth';
import { axiosInstance, publicAxios } from '../context/AuthContext';

const GalleryGridSection = ({ preview = false, onAuthRequired }) => {
  const { gallery, dispatch } = useArtworksContext();
  const { user } = useAuthContext();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateArtwork, setShowCreateArtwork] = useState(false);
  const [previewArtworks, setPreviewArtworks] = useState([]);
  const [containerRef, containerWidth] = useContainerWidth();

  const canEdit =
    !preview &&
    user?.permissions?.includes('curator');

  const galleryName = useMemo(() => {
    return gallery?.gallery?.displayName || 'My Gallery';
  }, [gallery?.gallery?.displayName]);

  const artworks = useMemo(() => {
    if (preview) return previewArtworks;
    return gallery?.artworks || [];
  }, [gallery?.artworks, preview, previewArtworks]);

  const shouldVirtualize = !preview && artworks.length > 20;

  useEffect(() => {
    const fetchArtworks = async () => {
      setIsLoading(true);
      setError(null);

      try {
        if (preview) {
          const response = await publicAxios.get('/artworks/preview?limit=12');
          setPreviewArtworks(response.data.artworks || []);
        } else if (user) {
          const response = await axiosInstance.get('/artworks?view=gallery');
          dispatch({ type: 'SET_ARTWORKS', payload: response.data });
        }
      } catch (err) {
        console.error('Error fetching gallery artworks:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchArtworks();
  }, [dispatch, user, preview]);

  const handleArtworkClick = () => {
    if (preview && onAuthRequired) {
      onAuthRequired();
    }
  };

  const addArtworkItem = canEdit && (
    showCreateArtwork ? (
      <ArtworkForm onCancel={() => setShowCreateArtwork(false)} />
    ) : (
      <div
        onClick={() => setShowCreateArtwork(true)}
        className="w-full max-w-80 mx-auto bg-white border-2 border-dashed border-gray-300 rounded-2xl shadow-lg overflow-hidden hover:shadow-xl hover:border-blue-400 transition-all duration-200 cursor-pointer group"
      >
        <div className="p-4 h-full flex flex-col items-center justify-center" style={{ minHeight: '420px' }}>
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors duration-200">
              <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <h3 className="font-semibold text-lg font-sans text-gray-700 group-hover:text-blue-600 transition-colors duration-200 mb-2">
              Add New Artwork
            </h3>
            <p className="text-sm text-gray-500">
              Click to add a new artwork to your gallery
            </p>
          </div>
        </div>
      </div>
    )
  );

  return (
    <section id="gallery" className="w-full py-12">
      <div className="self-center py-4 sm:py-8 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold headFont text-sky-950 uppercase">
          {preview ? 'Explore the Gallery' : galleryName}
        </h2>
        {preview && (
          <p className="text-gray-600 mt-2 max-w-2xl mx-auto">
            Browse featured artworks. Sign in to open analytics and manage your collection.
          </p>
        )}
      </div>

      {error && (
        <div className="mx-auto p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg max-w-md mb-4">
          <p>Error loading gallery: {error}</p>
        </div>
      )}

      <div ref={containerRef} className="px-8 sm:px-16 gallery">
        {isLoading ? (
          <div className="grid gap-10 lg:grid-cols-4">
            {Array.from({ length: 8 }, (_, index) => (
              <ArtworkSkeleton key={index} />
            ))}
          </div>
        ) : shouldVirtualize ? (
          <VirtualizedGallery
            artworks={artworks}
            containerWidth={containerWidth}
            trailingItem={addArtworkItem}
          />
        ) : (
          <div className="grid gap-10 lg:grid-cols-4 items-start">
            {artworks.map((artwork) => (
              <div
                key={artwork._id}
                onClick={preview ? handleArtworkClick : undefined}
                className={preview ? 'cursor-pointer' : undefined}
              >
                <ArtworkDetails artwork={artwork} disableNavigation={preview} />
              </div>
            ))}
            {addArtworkItem}
          </div>
        )}
      </div>

      {!isLoading && artworks.length === 0 && !error && !canEdit && (
        <div className="text-center py-8">
          <p className="text-gray-500 text-lg">No artworks found in this gallery.</p>
        </div>
      )}
    </section>
  );
};

export default GalleryGridSection;
